/**
 * HeadWithdrawHistory Component
 * 본사 입출금현황 페이지 - 협력사 입출금 내역 조회 및 입금/출금 신청
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHeadWithdrawData } from '../hooks/useHeadWithdrawData';
import { useUserInfo } from '@/shared/hooks/useCrudOperations';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import Loading from '@/shared/components/feedback/Loading';
import HeadWithdrawModal from './HeadWithdrawModal';
import HeadDepositModal from './HeadDepositModal';
import FilterSection from './FilterSection';
import HistoryAccordion, { ViewMode } from './HistoryAccordion';
import SummarySection from './SummarySection';
import {
    WithdrawHistoryFilters,
    createDefaultFilters
} from '../utils/withdrawHistory.helpers';
import '@/shared/styles/common-table.css';
import '../styles/HeadWithdrawHistory.css';

/** 필터 저장 키 */
const FILTER_STORAGE_KEY = 'headWithdrawFilters';

const HeadWithdrawHistory: React.FC = () => {
    // ======== Hooks ========
    const { getUserInfo } = useUserInfo();
    const userInfo = getUserInfo();
    const isHeadUser = userInfo?.userType === 'HEADQUARTERS';
    const isSupplierUser = userInfo?.userType === 'SUPPLIER';
    // 협력사 사용자의 협력사 ID: suppId가 없으면 userId를 사용 (협력사 계정은 userId가 협력사 ID인 경우가 있음)
    const userSuppId = userInfo?.suppId || (isSupplierUser ? userInfo?.userId : undefined);

    const {
        histories,
        currentBalanceByRider,
        currentBalanceBySupplier,
        withdrawableAmount,
        loading,
        downloading,
        withdrawFee,
        depositFee,
        accountInfo,
        supplierOptions,
        riderOptions,
        fetchHistories,
        fetchWithdrawableAmount,
        fetchAccountInfo,
        fetchSupplierOptions,
        fetchRiderOptions,
        requestWithdraw,
        requestDeposit,
        downloadExcel,
    } = useHeadWithdrawData();

    // 뷰 모드 상태 (협력사별 또는 라이더별)
    const [viewMode, setViewMode] = useState<ViewMode>('supplier');

    // 초기 로딩 상태 추가
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // ======== State ========
    /** 검색 필터 상태 */
    const [filters, setFilters] = useState<WithdrawHistoryFilters>(() => {
        const saved = loadFilters<WithdrawHistoryFilters>(FILTER_STORAGE_KEY);
        return saved || createDefaultFilters();
    });

    /** 출금 모달 열림 상태 */
    const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

    /** 입금 모달 열림 상태 */
    const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);

    /** 초기화 완료 여부 */
    const hasInitialized = useRef(false);

    // ======== Effects ========
    /** 출금 모달 열릴 때 출금가능금액 조회 */
    useEffect(() => {
        if (isWithdrawModalOpen && isHeadUser) {
            fetchWithdrawableAmount().catch(() => { /* 에러는 훅에서 처리 */ });
        }
    }, [isWithdrawModalOpen, isHeadUser, fetchWithdrawableAmount]);

    /** 입금 모달 열릴 때 계좌 정보 조회 */
    useEffect(() => {
        if (isDepositModalOpen && isHeadUser) {
            fetchAccountInfo(true).catch(() => { /* 에러는 훅에서 처리 */ });
        }
    }, [isDepositModalOpen, isHeadUser, fetchAccountInfo]);

    /** 협력사 옵션 목록 조회 (필터 날짜 변경 시)- 본사 사용자만 */
    useEffect(() => {
        if (filters.startDate && filters.endDate && !isSupplierUser) {
            fetchSupplierOptions(filters.startDate, filters.endDate).catch(() => { /* 에러는 훅에서 처리 */ });
        }
    }, [filters.startDate, filters.endDate, fetchSupplierOptions, isSupplierUser]);

    /** 라이더 옵션 목록 조회 (날짜 변경 시 또는 협력사 선택 시) */
    useEffect(() => {
        if (filters.startDate && filters.endDate) {
            // 협력사 사용자: 자신의 협력사 ID로 조회
            // 본사 사용자: 선택된 협력사명으로 조회 (없으면 전체)
            const supplierNameToUse = isSupplierUser ? userSuppId : (filters.supplierName || '');
            fetchRiderOptions(filters.startDate, filters.endDate, supplierNameToUse || '').catch(() => { /* 에러는 훅에서 처리 */ });
        }
    }, [filters.startDate, filters.endDate, filters.supplierName, fetchRiderOptions, isSupplierUser, userSuppId]);

    /** 초기 데이터 로드 */
    useEffect(() => {
        if (!hasInitialized.current && userInfo && (isHeadUser || (isSupplierUser && userSuppId))) {
            hasInitialized.current = true;
            const savedFilters = loadFilters<WithdrawHistoryFilters>(FILTER_STORAGE_KEY);
            const filtersToUse = savedFilters || filters;

            // 협력사 사용자일 경우 자신의 suppId로 필터링 (supplierName은 비워서 suppId로만 필터링)
            const supplierIdToUse = isSupplierUser ? userSuppId : undefined; // 본사는 suppId 보내지 않음
            const supplierNameToUse = isSupplierUser ? undefined : filtersToUse.supplierName;

            fetchHistories(
                filtersToUse.startDate,
                filtersToUse.endDate,
                undefined,
                undefined,
                supplierIdToUse,
                supplierNameToUse,
                undefined, // riderId는 보내지 않음
                filtersToUse.riderName
            ).catch(() => { /* 에러는 훅에서 처리 */ })
            .finally(() => {
                // 초기 데이터 로드 완료
                setIsInitialLoading(false);
            });
            
            // 예치금 잔액 조회 (본사만)
            if (isHeadUser) {
                fetchWithdrawableAmount().catch(() => { /* 에러는 훅에서 처리 */ });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo, isSupplierUser, userSuppId, isHeadUser]);

    // ======== Event Handlers ========
    /** 검색 실행 - 필터 저장 후 내역 조회 */
    const handleSearch = useCallback(() => {
        // 협력사 사용자일 경우 자신의 suppId로 필터링 (supplierName은 비워서 suppId로만 필터링)
        const supplierIdToUse = isSupplierUser ? userSuppId : undefined; // 본사는 suppId 보내지 않음
        const supplierNameToUse = isSupplierUser ? undefined : filters.supplierName;

        fetchHistories(
            filters.startDate,
            filters.endDate,
            undefined,
            undefined,
            supplierIdToUse,
            supplierNameToUse,
            undefined, // riderId는 보내지 않음
            filters.riderName
        );
        saveFilters(FILTER_STORAGE_KEY, filters);
    }, [filters, fetchHistories, isSupplierUser, userSuppId]);

    /** 출금 신청 처리 */
    const handleRequestWithdraw = useCallback(async (amount: number, memo?: string) => {
        if (!userInfo?.userId) return;

        const result = await requestWithdraw(userInfo.userId, amount, memo);
        if (result) {
            setIsWithdrawModalOpen(false);
            // 협력사 사용자일 경우 자신의 suppId로 필터링
            const supplierIdToUse = isSupplierUser ? userSuppId : undefined; // 본사는 suppId 보내지 않음
            const supplierNameToUse = isSupplierUser ? undefined : filters.supplierName;
            fetchHistories(
                filters.startDate,
                filters.endDate,
                undefined,
                undefined,
                supplierIdToUse,
                supplierNameToUse,
                undefined, // riderId는 보내지 않음
                filters.riderName
            );
        }
    }, [userInfo?.userId, requestWithdraw, filters, fetchHistories, isSupplierUser, userSuppId]);

    /** 입금 신청 처리 */
    const handleRequestDeposit = useCallback(async (amount: number, memo?: string) => {
        if (!userInfo?.userType) return;

        const result = await requestDeposit(userInfo.userType, amount, memo);
        if (result) {
            setIsDepositModalOpen(false);
            // 협력사 사용자일 경우 자신의 suppId로 필터링
            const supplierIdToUse = isSupplierUser ? userSuppId : undefined; // 본사는 suppId 보내지 않음
            const supplierNameToUse = isSupplierUser ? undefined : filters.supplierName;
            fetchHistories(
                filters.startDate,
                filters.endDate,
                undefined,
                undefined,
                supplierIdToUse,
                supplierNameToUse,
                undefined, // riderId는 보내지 않음
                filters.riderName
            );
        }
    }, [userInfo?.userType, requestDeposit, filters, fetchHistories, isSupplierUser, userSuppId]);

    /** 출금 모달 열기 */
    const handleWithdrawModalOpen = useCallback(() => {
        setIsWithdrawModalOpen(true);
    }, []);

    /** 엑셀 다운로드 */
    const handleExcelDownload = useCallback(() => {
        // 협력사 사용자일 경우 자신의 suppId로 필터링
        const supplierIdToUse = isSupplierUser ? userSuppId : undefined; // 본사는 suppId 보내지 않음
        const supplierNameToUse = isSupplierUser ? undefined : filters.supplierName;

        downloadExcel(
            filters.startDate,
            filters.endDate,
            undefined,
            undefined,
            supplierIdToUse,
            supplierNameToUse,
            undefined, // riderId는 보내지 않음
            filters.riderName
        );
    }, [downloadExcel, filters, isSupplierUser, userSuppId]);

    // ======== Render ========
    // 엑셀 다운로드 중
    if (downloading) {
        return <Loading message="엑셀 다운로드 중..." />;
    }

    // 초기 로딩 중이거나 일반 로딩 중일 때 로딩 화면 표시
    if (isInitialLoading || loading) {
        return <Loading message="입출금 내역을 불러오는 중..." />;
    }

    return (
        <div className="head-withdraw-history">
            {/* 페이지 헤더 */}
            <div className="page-header">
                <h1 className="page-title">입출금 내역</h1>
            </div>

            {/* 검색 필터 섹션 */}
            <FilterSection
                filters={filters}
                isHeadUser={isHeadUser}
                isSupplierUser={isSupplierUser}
                supplierOptions={supplierOptions}
                riderOptions={riderOptions}
                onFilterChange={setFilters}
                onSearch={handleSearch}
                onWithdrawClick={handleWithdrawModalOpen}
                onSupplierChange={(fromDate, toDate, supplierName) => {
                    fetchRiderOptions(fromDate, toDate, supplierName || '');
                }}
                onExcelDownload={handleExcelDownload}
            />

            {/* 합계 섹션 */}
            <SummarySection
                histories={histories}
                withdrawableAmount={withdrawableAmount}
            />

            {/* 검색 결과 건수 (SD, RD, F 제외) */}
            <div className="result-count">
                <i className="fas fa-list"></i>
                총 <strong>{(histories || []).filter(h => !['SD', 'RD', 'F'].includes(h.acctType)).length.toLocaleString()}건</strong>
            </div>

            {/* 입출금 내역 아코디언 */}
            <HistoryAccordion
                histories={histories || []}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isSupplierUser={isSupplierUser}
                userSuppId={userSuppId}
                currentBalanceByRider={currentBalanceByRider}
                currentBalanceBySupplier={currentBalanceBySupplier}
            />

            {/* 입금신청 모달 */}
            {isHeadUser && isDepositModalOpen && (
                <HeadDepositModal
                    withdrawableAmount={withdrawableAmount}
                    depositFee={depositFee}
                    accountInfo={accountInfo}
                    onClose={() => setIsDepositModalOpen(false)}
                    onRequest={handleRequestDeposit}
                />
            )}

            {/* 출금신청 모달 */}
            {isHeadUser && isWithdrawModalOpen && (
                <HeadWithdrawModal
                    withdrawableAmount={withdrawableAmount}
                    withdrawFee={withdrawFee}
                    accountInfo={accountInfo}
                    onClose={() => setIsWithdrawModalOpen(false)}
                    onRequest={handleRequestWithdraw}
                />
            )}
        </div>
    );
};

export default HeadWithdrawHistory;
